/**
 * Plan and billing on the web (docs/PAYMENTS.md). The card is typed only on Stripe's own page (Checkout); managing it,
 * the invoices and canceling happen on Stripe's Portal. Nothing here ever holds card data (PCI DSS SAQ A).
 * - StartTrialCard: "Almost there!" at a held Save (or "Keep it going: start your 25 free days" before a value), the price in
 *   one line, "Add a card", "Not now".
 * - BillingPrompts (in the layout): the card ask when a Save is held for the card (api.ts CARD_TO_SAVE_EVENT; services/api
 *   heldentries.ts), the before-value asks (lib/cardPrompt.ts) and the calm banners.
 * - BillingStart / BillingDone: the full page, and the page Stripe sends people back to.
 * - The plan page itself: screens/PlanBilling.tsx (/billing).
 * Owners and admins only. Staff never see billing, and nothing here can block a check-in, a sick report or a step.
 */
import { Suspense, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ALWAYS_WORKS, CARD_TO_SAVE, VALUE_WORDS, shiftDay, PLAN_BAND_LABEL, PLAN_WORDS, dayIn, graceLine, timeZoneOf, TRIAL_PERIOD_DAYS, trialPeriodDaysFor, bannerShows, waitlistHoldWords, cardPromptDecision, cardPromptWords, dollarsOf, noCardBannerShows, prettyDay, promptNotNow, promptShown, renewalTerms, trialEndsOnFrom,
  type CardPromptMemory, type CardPromptTrigger,
} from "@logwave/core";
import type { BillingViewResponse, HeldInfo, PlanResponse, WaitlistStatusResponse } from "../api.ts";
import { CARD_TO_SAVE_EVENT, HELD_CHANGED_EVENT, isHomeOrg } from "../api.ts";
// "Invite the owner to take over" is for the few who started the account for someone else: its own chunk, not the main one.
import { lazyInline, lazyNamed } from "../lib/lazy.ts";
const InviteOwner = lazyNamed(() => import("./InviteOwner.tsx"), "InviteOwner");
import { loadPromptMemory, savePromptMemory, setCardPromptHost } from "../lib/cardPrompt.ts";
import { PLAN_NOT_HERE, viaApp } from "../lib/storeApp.ts";
import { leadOffer, storedLeadCode } from "../lib/leadCode.ts";
import { localDate } from "../lib/time.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useSavedCount } from "../lib/savedCount.ts";
import { useApi, useSession } from "../session.tsx";
import { DEMO_PAY_ACTION, demoSignupUrl, isDemoSample } from "../lib/demoAsk.ts";
import { clearBuy, useFinishSetup } from "../lib/buyIntent.ts";
import type { BuyAddon } from "@logwave/core";
import { Icon } from "./Icons.tsx";

// A home's card ask loads when it is first drawn (the main chunk stays inside its budget).
const HomePlanBanner = lazyInline(() => import("./HomePlanBanner.tsx"), "HomePlanBanner");
import { CountyHoldCard, useCountyHold } from "./CountyHold.tsx";
import { AnchorButton, Button, Card, ErrorBox, Page, Section, Sheet, Skeleton } from "./ui.tsx";

/** Stripe's page is outside the app; the demo answers an in-app path. */
export function openBillingUrl(url: string, nav: (to: string) => void) {
  if (viaApp()) return; // opened from the phone app: never Stripe (lib/storeApp.ts)
  if (url.startsWith("/")) nav(url);
  else window.location.assign(url);
}

/**
 * Opens Checkout (or says plainly why it can't). `buy`: the site buyer path (lib/buyIntent.ts): the button's label rides along for
 * the funnel, Stripe returns to the buyer pages, and the kept intent is let go once Stripe's page opens (no loop back into it).
 * A double tap is harmless: the button is busy after the first, and the server answers a repeat with the same session.
 */
function useStartTrial(buy?: { src?: string }) {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const start = useCallback(async () => {
    setBusy(true); setError(undefined);
    // A checklist code from the email (lib/leadCode.ts): 10% off the first paid month. Without one this is the same call as before.
    try {
      const { url } = await api.billingCheckout(s.org.id, storedLeadCode() ?? undefined, buy ? { from: "buy", ...(buy.src ? { src: buy.src } : {}) } : undefined);
      if (buy) clearBuy();
      openBillingUrl(url, nav);
    }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }, [api, s.org.id, nav, buy?.src]); // eslint-disable-line react-hooks/exhaustive-deps
  return { start, busy, error };
}

export function useOpenPortal() {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const open = useCallback(async () => {
    setBusy(true); setError(undefined);
    try { openBillingUrl((await api.billingPortal(s.org.id)).url, nav); setBusy(false); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }, [api, s.org.id, nav]);
  return { open, busy, error };
}

/** The checklist code's offer, shown only when the server says the code works right now. */
function useLeadOffer() {
  const [offer, setOffer] = useState<{ percent: number; months: number } | null>(null);
  useEffect(() => {
    const code = storedLeadCode();
    if (!code) return;
    let live = true;
    void leadOffer(code).then((o) => { if (live) setOffer(o); });
    return () => { live = false; };
  }, []);
  return offer;
}

/** The card itself: one next step ("Add a card"), everything else short. */
export function StartTrialCard({ billing, trigger, onNotNow, heading = true, hold, onOpened, buy, lede }: { billing: BillingViewResponse; trigger?: CardPromptTrigger; onNotNow: () => void; heading?: boolean; /** The page's own reason, in the person's numbers (BillingStart: the entries waiting for the card). */ lede?: string; /** Not open in this county yet (components/CountyHold.tsx): the waitlist card shows instead of Checkout. */ hold?: WaitlistStatusResponse | null; onOpened?: () => void; /** The site buyer path (screens/BuyCard.tsx). */ buy?: { src?: string } }) {
  const t = useStartTrial(buy);
  const offer = useLeadOffer();
  const s = useSession();
  const words = cardPromptWords(trigger ?? "binder", billing.monthlyCents, s.org.kind);
  const home = isHomeOrg(s.org);
  // A canceled plan starting again has no second free period (its price line and terms say what is charged today).
  const again = billing.state === "canceled";
  const remindIn = home ? "the day before" : "3 days before";
  if (hold) return <CountyHoldCard status={hold} heading={heading} onNotNow={onNotNow} onOpened={onOpened} />;
  const saving = trigger === "save";
  // The renewal terms before the card (docs/legal/EASY-CANCEL.md): the first charge date, the monthly price until canceled, and how
  // to cancel online. A canceled plan starting again has no second free period, so its first charge is today.
  const terms = renewalTerms({ monthlyCents: billing.monthlyCents, trialEndsOn: again ? null : trialEndsOnFrom(new Date(), timeZoneOf(s.org.state), s.org.kind), kind: s.org.kind });
  return (
    <div className="stack" data-testid="start-trial">
      {heading && <h2>{trigger ? words.title : again ? PLAN_WORDS.restartTitle : `Start your ${trialPeriodDaysFor(s.org.kind)} free days`}</h2>}
      <p className="lede" data-testid={lede ? "start-trial-why" : undefined}>{lede ?? (trigger ? words.lede : home ? "Your home's lists, the fridge check and the food photos, all in one plan." : "Every check, your binder, your team and Busy hours, all in one place.")}</p>
      <p data-testid="price-line"><strong>{billing.priceLine}</strong></p>
      {saving && <p className="small" data-testid="free-days">{CARD_TO_SAVE.freeDays}</p>}
      {offer && <p className="small" data-testid="lead-offer"><Icon name="check" size={16} /> Your checklist code takes {offer.percent}% off your first month.</p>}
      {!again && <p className="small"><Icon name="clock" size={16} /> We'll remind you {remindIn}.</p>}
      <ul className="small stack-sm" data-testid="renewal-terms">{terms.map((x) => <li key={x}>{x}</li>)}</ul>
      <ErrorBox error={t.error} />
      <div className="form-actions">
        <Button big icon="check" busy={t.busy} disabled={t.busy} onClick={() => void t.start()} data-testid="start-trial-add-card">{t.busy ? "Opening Stripe…" : words.action}</Button>
        <Button variant="ghost" onClick={onNotNow}>{words.later}</Button>
      </div>
      {saving && <p className="small" data-testid="not-now-keeps">{CARD_TO_SAVE.notNowKeeps}</p>}
      <p className="caption">You type your card on Stripe's secure page. We never see it. {ALWAYS_WORKS}</p>
    </div>
  );
}

/* ─────────────── in the layout: the prompt and the banners ─────────────── */

const onToday = (path: string) => path === "/" || path === "/today";

/** Opened from the phone app (lib/storeApp.ts): no card ask and no banners at all in this tab. */
export function BillingPrompts() {
  const s = useSession();
  return viaApp() ? null : isHomeOrg(s.org) ? <HomePlanBanner /> : <BillingPromptsHere />;
}

function BillingPromptsHere() {
  const s = useSession();
  const api = useApi();
  const nav = useNavigate();
  const loc = useLocation();
  // The sample café has no card to add: its asks are the sign-up links in lib/demoAsk.ts, never a fake trial.
  const admin = s.can("admin") && !isHomeOrg(s.org) && !isDemoSample(s.org);
  const who = s.me.user.id || s.me.user.email;
  const billing = useLoad<BillingViewResponse | null>(() => (admin ? api.billing(s.org.id) : Promise.resolve(null)), [api, s.org.id, admin]);
  const [memory, setMemory] = useState<CardPromptMemory>(() => loadPromptMemory(s.org.id, who));
  useEffect(() => { setMemory(loadPromptMemory(s.org.id, who)); }, [s.org.id, who]);
  const [open, setOpen] = useState<{ trigger: CardPromptTrigger; done?: () => void; held?: HeldInfo } | null>(null);
  const portal = useOpenPortal();
  // The trial's last days (the trial_ending note on Today): what this business saved since its first free day (core VALUE_WORDS).
  const trialFrom = billing.data?.banner?.kind === "trial_ending" && billing.data.trialEndsOn ? shiftDay(billing.data.trialEndsOn, -trialPeriodDaysFor(s.org.kind)) : null;
  const saved = useSavedCount(s.org.id, trialFrom, !!trialFrom && onToday(loc.pathname));
  // Not open in this county yet (docs/WAITLIST.md): the card prompt shows "Coming to <County>", and the "Add a card" banner stays away.
  const county = useCountyHold(admin);

  const remember = useCallback((m: CardPromptMemory) => { setMemory(m); savePromptMemory(s.org.id, who, m); }, [s.org.id, who]);
  const show = useCallback((trigger: CardPromptTrigger, done?: () => void, held?: HeldInfo) => {
    remember(promptShown(loadPromptMemory(s.org.id, who), trigger, localDate()));
    setOpen({ trigger, done, ...(held ? { held } : {}) });
  }, [remember, s.org.id, who]);

  // Register the host the api wrapper talks to (lib/cardPrompt.ts): the before-value asks.
  useEffect(() => {
    if (!admin || !billing.data) { setCardPromptHost(null); return; }
    const state = billing.data.state;
    setCardPromptHost({
      orgId: s.org.id,
      wants: (trigger) => cardPromptDecision({ state, canPay: true, memory: loadPromptMemory(s.org.id, who), today: localDate(), trigger }),
      before: (trigger) => new Promise<void>((resolve) => show(trigger, resolve)),
    });
    return () => setCardPromptHost(null);
  }, [admin, billing.data, s.org.id, who, show]);

  // On the plan pages (Stripe's way back, the start page) the ask has done its job: it closes.
  useEffect(() => {
    if (!loc.pathname.startsWith("/billing") || !open) return;
    const d = open.done;
    setOpen(null);
    d?.();
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // A Save held for the card (services/api heldentries.ts): "Almost there!" at once, every time (it says where the entry went).
  useEffect(() => {
    if (!admin) return;
    const on = (e: Event) => show("save", undefined, (e as CustomEvent<HeldInfo | undefined>).detail);
    try { window.addEventListener(CARD_TO_SAVE_EVENT, on); } catch { return; }
    return () => window.removeEventListener(CARD_TO_SAVE_EVENT, on);
  }, [admin, show]);

  if (!admin || !billing.data) return null;
  const b = billing.data;
  const notNow = () => { remember(promptNotNow(loadPromptMemory(s.org.id, who))); const d = open?.done; setOpen(null); d?.(); };
  const banner = b.banner && bannerShows(b.banner.kind, onToday(loc.pathname)) && (b.banner.kind !== "no_card" || (noCardBannerShows(memory) && !county.hold && !county.loading)) && !loc.pathname.startsWith("/billing") ? b.banner : null;
  const act = () => {
    if (!banner) return;
    if (banner.kind === "past_due") void portal.open();
    else if (banner.kind === "trial_ending") nav("/billing");
    else nav("/billing/start");
  };
  return (
    <>
      {banner && (
        <div className={`offline billing-banner billing-${banner.kind}`} role="status" data-testid="billing-banner">
          {/* In the grace (core paywall.ts): the day it locks, and "Fix your card", one tap to Stripe's Portal. */}
          <p><Icon name={banner.kind === "past_due" ? "alert" : "info"} />{banner.kind === "past_due" && s.plan?.graceEndsAt ? graceLine(prettyDay(dayIn(s.plan.graceEndsAt, timeZoneOf(s.org.state)))) : banner.text}{" "}
            <Button small variant="secondary" busy={portal.busy} onClick={act} data-testid="billing-banner-action">{banner.kind === "past_due" ? PLAN_WORDS.fixCard : banner.action}</Button></p>
          {banner.kind === "trial_ending" && trialFrom && saved ? <p className="small" data-testid="trial-saved">{VALUE_WORDS.savedSince(saved, prettyDay(trialFrom))}</p> : null}
          {portal.error && <p className="small">{portal.error}</p>}
        </div>
      )}
      {open && (
        <Sheet title={county.hold ? waitlistHoldWords(county.hold.countyLabel).title : cardPromptWords(open.trigger, b.monthlyCents).title} onClose={notNow} closeLabel="Not now">
          <StartTrialCard billing={b} trigger={open.trigger} onNotNow={notNow} heading={false} hold={county.hold} onOpened={county.reload} />
          <Suspense fallback={null}><InviteOwner /></Suspense>
        </Sheet>
      )}
    </>
  );
}

/* ─────────────── the pages ─────────────── */

/** /billing/start: the same card as a page (from the banner, Settings, or Stripe's "back"). */
export function BillingStart() {
  return viaApp() ? <PlanNotHere /> : <BillingStartHere />;
}

/** /billing/start or /billing/done reached in the phone app's browser: plain words, no checkout (lib/storeApp.ts). */
function PlanNotHere() {
  const nav = useNavigate();
  return (
    <Page title="Plan and billing" narrow>
      <Card className="stack"><p className="lede" data-testid="plan-not-here">{PLAN_NOT_HERE}</p><div><Button big onClick={() => nav("/")}>Go to Today</Button></div></Card>
    </Page>
  );
}

function BillingStartHere() {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // The launch email's button (docs/WAITLIST.md) names the business: open that one when this person belongs to it.
  const wanted = params.get("org");
  const switching = !!wanted && wanted !== s.org.id && s.me.memberships.some((m) => m.org.id === wanted);
  useEffect(() => { if (switching && wanted) s.selectOrg(wanted); }, [switching, wanted]); // eslint-disable-line react-hooks/exhaustive-deps
  const billing = useLoad(() => api.billing(s.org.id), [api, s.org.id]);
  const county = useCountyHold(!isDemoSample(s.org) && !switching);
  // Most people reach this page from "N entries waiting for your card" (components/HeldEntries.tsx): the page says that, in their
  // number, instead of a feature list (CONVERSION-2026-10-13.md C5). No answer: the page reads as before.
  const waiting = useLoad(() => (isDemoSample(s.org) ? Promise.resolve(0) : api.heldEntries(s.org.id).then((r) => (r.canSave ? 0 : r.waiting), () => 0)), [api, s.org.id]);
  if (isDemoSample(s.org)) return <SampleNoCard where="billing" />;
  const held = billing.data?.canStart ? county.hold : null;
  const n = waiting.data ?? 0;
  return (
    <Page title={held ? waitlistHoldWords(held.countyLabel).title : n > 0 ? CARD_TO_SAVE.title : billing.data?.state === "canceled" ? PLAN_WORDS.restartTitle : `Start your ${trialPeriodDaysFor(s.org.kind)} free days`} narrow>
      {params.get("canceled") && <Card edge="info"><p>No card was added. That's fine: you can do it any time.</p></Card>}
      {billing.error ? <ErrorBox error={billing.error} onRetry={() => void billing.reload()} /> : !billing.data || switching || county.loading ? <Skeleton rows={3} /> : billing.data.canStart ? (
        <Card className="stack"><StartTrialCard billing={billing.data} onNotNow={() => nav("/")} heading={false} hold={held} onOpened={county.reload} {...(n > 0 ? { trigger: "save" as const, lede: VALUE_WORDS.waitingLede(n) } : {})} /><Suspense fallback={null}><InviteOwner /></Suspense></Card>
      ) : (
        <Card className="stack">
          <p><strong>{billing.data.line}</strong></p>
          <div><Button big onClick={() => nav("/")}>Go to Today</Button></div>
        </Card>
      )}
    </Page>
  );
}

/** A pay button in the sample: sign-up for the visitor's own place with the buy intent (never a card for the sample). */
export function DemoPayButton({ addon, big, small }: { addon?: BuyAddon; big?: boolean; small?: boolean }) {
  const s = useSession();
  return (
    <AnchorButton big={big} small={small} href={demoSignupUrl("billing", undefined, { buy: true, ...(addon ? { addon } : {}), state: s.org.state })} icon="chevron" testId="demo-pay">{DEMO_PAY_ACTION}</AnchorButton>
  );
}

/** /billing/start and /billing/done in the sample café: no card is taken here, and no trial starts. */
function SampleNoCard({ where: _where }: { where: "billing" }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const addon = params.get("addon");
  return (
    <Page title="This is a sample café" narrow>
      <Card className="stack" edge="info">
        <p className="lede">No card is taken here and no free days start. Your real {TRIAL_PERIOD_DAYS} days start when you set up your own place.</p>
        <div className="form-actions">
          <DemoPayButton big addon={addon === "ai_photos" || addon === "ai_roomcheck" ? addon : undefined} />
          <Button variant="ghost" onClick={() => nav("/")}>Back to the sample</Button>
        </div>
      </Card>
    </Page>
  );
}

/** /billing/done: Stripe sends people back here. The status comes from Stripe's webhook, so it may take a moment. */
export function BillingDone() {
  return viaApp() ? <PlanNotHere /> : <BillingDoneHere />;
}

function BillingDoneHere() {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  const [b, setB] = useState<BillingViewResponse>();
  const [waited, setWaited] = useState(false);
  // Entries that waited for the card (services/api heldentries.ts): saved the moment Stripe says the plan runs.
  const [savedLine, setSavedLine] = useState<string>();
  const sample = isDemoSample(s.org);
  const [params] = useSearchParams();
  // The site buyer path (lib/buyIntent.ts): the rest of the normal setup comes next, card or not.
  const finish = useFinishSetup(s.org.id);
  useEffect(() => {
    if (sample) return;
    let live = true, tries = 0;
    const poll = async () => {
      try {
        const v = await api.billing(s.org.id);
        if (!live) return;
        setB(v);
        if (v.state === "trialing" || v.state === "active") {
          try {
            const r = await api.saveHeld(s.org.id);
            if (live && r.saved?.length) setSavedLine(r.line ?? CARD_TO_SAVE.savedLine(r.saved.length));
            try { window.dispatchEvent(new CustomEvent(HELD_CHANGED_EVENT)); } catch { /* no window */ }
          } catch { /* the waiting line tries again on the next screen */ }
          return;
        }
      } catch { /* try again */ }
      if (++tries < 8 && live) setTimeout(() => void poll(), 1500); else if (live) setWaited(true);
    };
    void poll();
    return () => { live = false; };
  }, [api, s.org.id, sample]);
  if (sample) return <SampleNoCard where="billing" />;
  const started = b && (b.state === "trialing" || b.state === "active");
  // "started=1" only once Stripe's webhook says so: the next screen never claims free days that haven't begun.
  const next = params.get("from") === "invite" ? { to: "/people/add", label: "Invite your team" } : params.get("from") === "buy" && finish ? { to: `/setup/finish${started ? "?started=1" : ""}`, label: "Finish setting up" } : { to: "/", label: "Go to Today" };
  return (
    <Page title={started ? "You're all set" : "Almost there"} narrow>
      <Card className="stack" edge={started ? "go" : "info"}>
        {started ? (
          <>
            <p className="lede" data-testid="billing-done">Your {trialPeriodDaysFor(s.org.kind)} free days have started.</p>
            {savedLine && <p data-testid="held-saved"><Icon name="check" size={16} /> {savedLine}</p>}
            {b.trialEndsOn && <p>They end on {prettyDay(b.trialEndsOn)}. We'll remind you {isHomeOrg(s.org) ? "the day before" : "3 days before"}. Cancel anytime before and you pay nothing.</p>}
          </>
        ) : waited ? (
          <p className="lede">Stripe is still finishing. This can take a minute. You can keep working: nothing waits on it.</p>
        ) : <Skeleton rows={2} label="Checking with Stripe" />}
        <div><Button big icon="chevron" iconEnd onClick={() => nav(next.to, { replace: true })}>{next.label}</Button></div>
      </Card>
    </Page>
  );
}

/* Plan and billing is its own page now: screens/PlanBilling.tsx (owner, 2026-10-07). */

/** "2 locations · Small, Solo" */
export function summarizeBands(p: Pick<PlanResponse, "locations">): string {
  const n = p.locations.length;
  return `${n === 1 ? "1 location" : `${n} locations`} · ${p.locations.map((l) => PLAN_BAND_LABEL[l.band]).join(", ")}`;
}

